import { Link, useParams } from 'react-router-dom';
import { AdSlot } from '../components/AdSlot';
import { RecipeIllustration } from '../components/RecipeIllustration';
import { getRecipeBySlug, recipes } from '../data/recipes';
import { usePageMeta } from '../lib/meta';
import { SITE_URL } from '../config';
import { NotFoundPage } from './NotFoundPage';

export function RecipeDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const recipe = slug ? getRecipeBySlug(slug) : undefined;

  // Hooks must run unconditionally; fall back to neutral meta when missing.
  usePageMeta({
    title: recipe ? recipe.title : 'Recipe not found',
    description: recipe ? recipe.description : 'This recipe is not in the collection.',
    path: recipe ? `/recipes/${recipe.slug}` : '/recipes',
    jsonLd: recipe
      ? {
          '@context': 'https://schema.org',
          '@type': 'Recipe',
          name: recipe.title,
          description: recipe.description,
          url: `${SITE_URL}/recipes/${recipe.slug}`,
          recipeCategory: 'Omelette',
          recipeCuisine: 'Peafowl',
          keywords: recipe.tags.join(', '),
          recipeYield: `${recipe.servings} servings`,
          prepTime: `PT${recipe.prepMinutes}M`,
          cookTime: `PT${recipe.cookMinutes}M`,
          totalTime: `PT${recipe.prepMinutes + recipe.cookMinutes}M`,
          recipeIngredient: recipe.ingredients.map(
            (i) => `${i.quantity} ${i.item}${i.note ? ` (${i.note})` : ''}`,
          ),
          recipeInstructions: recipe.steps.map((step, idx) => ({
            '@type': 'HowToStep',
            position: idx + 1,
            text: step,
          })),
          author: { '@type': 'Organization', name: 'PeacockOmlettes.com' },
        }
      : undefined,
  });

  if (!recipe) return <NotFoundPage />;

  const index = recipes.findIndex((r) => r.slug === recipe.slug);
  const prev = index > 0 ? recipes[index - 1] : undefined;
  const next = index < recipes.length - 1 ? recipes[index + 1] : undefined;

  return (
    <article className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <ol className="flex flex-wrap items-center gap-2 text-plume-700">
          <li>
            <Link to="/" className="underline-offset-4 hover:underline">
              Home
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li>
            <Link to="/recipes" className="underline-offset-4 hover:underline">
              Recipes
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="font-semibold text-plume-950">
            {recipe.title}
          </li>
        </ol>
      </nav>

      <header className="mt-6">
        <RecipeIllustration
          accent={recipe.accent}
          variant={recipe.variant}
          className="aspect-[5/3] w-full rounded-3xl shadow-gloss-lg sm:aspect-[2/1]"
        />
        <h1 className="mt-8 font-display text-3xl text-plume-950 sm:text-5xl">{recipe.title}</h1>
        <p className="mt-3 text-lg italic text-bronze-700">{recipe.tagline}</p>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-plume-800">
          {recipe.description}
        </p>

        <dl className="mt-8 grid grid-cols-2 gap-4 rounded-2xl bg-white p-6 shadow-gloss sm:grid-cols-4">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-plume-700">Prep</dt>
            <dd className="mt-1 font-display text-xl text-plume-950">{recipe.prepMinutes} min</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-plume-700">Cook</dt>
            <dd className="mt-1 font-display text-xl text-plume-950">{recipe.cookMinutes} min</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-plume-700">
              Serves
            </dt>
            <dd className="mt-1 font-display text-xl text-plume-950">{recipe.servings}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-plume-700">
              Effort
            </dt>
            <dd className="mt-1 font-display text-xl text-plume-950">{recipe.difficulty}</dd>
          </div>
        </dl>
      </header>

      <div className="mt-10 grid gap-10 md:grid-cols-[2fr_3fr]">
        <section aria-labelledby="ingredients-heading">
          <h2 id="ingredients-heading" className="font-display text-2xl text-plume-950">
            Ingredients
          </h2>
          <ul className="mt-4 space-y-3">
            {recipe.ingredients.map((ing) => (
              <li
                key={`${ing.quantity}-${ing.item}`}
                className="rounded-xl bg-white px-4 py-3 shadow-gloss"
              >
                <span className="font-semibold text-plume-950">
                  {ing.quantity} {ing.item}
                </span>
                {ing.note && (
                  <span className="block text-sm italic text-bronze-700">{ing.note}</span>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="method-heading">
          <h2 id="method-heading" className="font-display text-2xl text-plume-950">
            Method
          </h2>
          <ol className="mt-4 space-y-5">
            {recipe.steps.map((step, idx) => (
              <li key={idx} className="flex gap-4">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-plume-700 font-display text-white"
                >
                  {idx + 1}
                </span>
                <p className="leading-relaxed text-plume-900">{step}</p>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <aside className="mt-12 rounded-2xl border-l-4 border-gold-500 bg-white p-6 shadow-gloss">
        <h2 className="font-display text-lg text-plume-950">From the chef&rsquo;s notebook</h2>
        <p className="mt-2 leading-relaxed text-plume-800">{recipe.chefNote}</p>
      </aside>

      <ul aria-label="Recipe tags" className="mt-8 flex flex-wrap gap-2">
        {recipe.tags.map((tag) => (
          <li key={tag}>
            <Link
              to={`/recipes?q=${encodeURIComponent(tag)}`}
              className="inline-block rounded-full bg-tail-100 px-4 py-1.5 text-sm font-semibold text-tail-800 transition-colors hover:bg-tail-200"
            >
              {tag}
            </Link>
          </li>
        ))}
      </ul>

      <AdSlot slot="inArticle" />

      <nav
        aria-label="More recipes"
        className="mt-4 flex flex-col gap-4 sm:flex-row sm:justify-between"
      >
        {prev ? (
          <Link
            to={`/recipes/${prev.slug}`}
            className="group rounded-2xl bg-white px-5 py-4 shadow-gloss transition-shadow hover:shadow-gloss-lg"
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-plume-700">
              ← Previous
            </span>
            <span className="block font-display text-plume-950 group-hover:text-plume-700">
              {prev.title}
            </span>
          </Link>
        ) : (
          <span />
        )}
        {next && (
          <Link
            to={`/recipes/${next.slug}`}
            className="group rounded-2xl bg-white px-5 py-4 text-right shadow-gloss transition-shadow hover:shadow-gloss-lg"
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-plume-700">
              Next →
            </span>
            <span className="block font-display text-plume-950 group-hover:text-plume-700">
              {next.title}
            </span>
          </Link>
        )}
      </nav>
    </article>
  );
}
